import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './pages/Login';
import SchoolSignup from './pages/SchoolSignup';
import AdminDashboard from './pages/AdminDashboard';
import Students from './pages/admin/Students';
import Teachers from './pages/admin/Teachers';
import Classes from './pages/admin/Classes';
import Subjects from './pages/admin/Subjects';
import Results from './pages/admin/Results';
import Assignments from './pages/admin/Assignments';
import Attendance from './pages/admin/Attendance';
import Timetable from './pages/admin/Timetable';
import Notices from './pages/admin/Notices';
import Fees from './pages/admin/Fees';
import SchoolPayments from './pages/admin/SchoolPayments';
import StudentDashboard from './pages/StudentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import ParentDashboard from './pages/ParentDashboard';
import TeacherAttendance from './pages/teacher/Attendance';
import TeacherResults from './pages/teacher/Results';
import TeacherAssignments from './pages/teacher/Assignments';
import TeacherNotices from './pages/teacher/Notices';
import TeacherTimetable from './pages/teacher/Timetable';
import TeacherProfile from './pages/teacher/Profile';
import TeacherSettings from './pages/teacher/Settings';
import TeacherClasses from './pages/teacher/Classes';
import TeacherStudents from './pages/teacher/Students';
import StudentAssignments from './pages/student/Assignments';
import StudentResults from './pages/student/Results';
import StudentProfile from './pages/student/Profile';
import StudentTimetable from './pages/student/Timetable';
import StudentNotices from './pages/student/Notices';
import StudentSettings from './pages/student/Settings';
import StudentAttendance from './pages/student/Attendance';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminPayments from './pages/SuperAdminPayments';
import PaymentPage from './pages/PaymentPage';
import ParentResults from './pages/parent/Results';
import ParentAttendance from './pages/parent/Attendance';
import ParentFees from './pages/parent/Fees';
import './index.css';

const PrivateRoute: React.FC<{ children: React.ReactElement; allowedRoles?: string[] }> = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

const SuperAdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const token = localStorage.getItem('superAdminToken');
  
  if (!token) {
    return <Navigate to="/superadmin/login" />;
  }
  
  return children;
};

const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  const getDashboard = () => {
    if (!user) return <Navigate to="/login" />;
    
    switch (user.role) {
      case 'admin':
        return <AdminDashboard />;
      case 'student':
        return <StudentDashboard />;
      case 'teacher':
        return <TeacherDashboard />;
      case 'parent':
        return <ParentDashboard />;
      default:
        return <Navigate to="/login" />;
    }
  };

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SchoolSignup />} />
      <Route path="/payment/:token" element={<PaymentPage />} />
      <Route path="/superadmin/login" element={<SuperAdminLogin />} />
      <Route path="/superadmin/dashboard" element={
        <SuperAdminRoute>
          <SuperAdminDashboard />
        </SuperAdminRoute>
      } />
      <Route path="/superadmin/payments" element={
        <SuperAdminRoute>
          <SuperAdminPayments />
        </SuperAdminRoute>
      } />
      <Route path="/" element={
        <PrivateRoute>
          {getDashboard()}
        </PrivateRoute>
      } />
      <Route path="/admin" element={
        <PrivateRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </PrivateRoute>
      } />
      <Route path="/admin/students" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Students />
        </PrivateRoute>
      } />
      <Route path="/admin/teachers" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Teachers />
        </PrivateRoute>
      } />
      <Route path="/admin/classes" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Classes />
        </PrivateRoute>
      } />
      <Route path="/admin/subjects" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Subjects />
        </PrivateRoute>
      } />
      <Route path="/admin/results" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Results />
        </PrivateRoute>
      } />
      <Route path="/admin/assignments" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Assignments />
        </PrivateRoute>
      } />
      <Route path="/admin/attendance" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Attendance />
        </PrivateRoute>
      } />
      <Route path="/admin/timetable" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Timetable />
        </PrivateRoute>
      } />
      <Route path="/admin/notices" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Notices />
        </PrivateRoute>
      } />
      <Route path="/admin/fees" element={
        <PrivateRoute allowedRoles={['admin']}>
          <Fees />
        </PrivateRoute>
      } />
      <Route path="/admin/payments" element={
        <PrivateRoute allowedRoles={['admin']}>
          <SchoolPayments />
        </PrivateRoute>
      } />
      
      {/* Teacher Routes */}
      <Route path="/teacher" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherDashboard />
        </PrivateRoute>
      } />
      <Route path="/teacher/classes" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherClasses />
        </PrivateRoute>
      } />
      <Route path="/teacher/students" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherStudents />
        </PrivateRoute>
      } />
      <Route path="/teacher/attendance" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherAttendance />
        </PrivateRoute>
      } />
      <Route path="/teacher/results" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherResults />
        </PrivateRoute>
      } />
      <Route path="/teacher/assignments" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherAssignments />
        </PrivateRoute>
      } />
      <Route path="/teacher/notices" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherNotices />
        </PrivateRoute>
      } />
      <Route path="/teacher/timetable" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherTimetable />
        </PrivateRoute>
      } />
      <Route path="/teacher/profile" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherProfile />
        </PrivateRoute>
      } />
      <Route path="/teacher/settings" element={
        <PrivateRoute allowedRoles={['teacher']}>
          <TeacherSettings />
        </PrivateRoute>
      } />
      
      {/* Student Routes */}
      <Route path="/student" element={
        <PrivateRoute allowedRoles={['student']}>
          <StudentDashboard />
        </PrivateRoute>
      } />
      <Route path="/student/assignments" element={
        <PrivateRoute allowedRoles={['student']}>
          <StudentAssignments />
        </PrivateRoute>
      } />
      <Route path="/student/results" element={
        <PrivateRoute allowedRoles={['student']}>
          <StudentResults />
        </PrivateRoute>
      } />
      <Route path="/student/profile" element={
        <PrivateRoute allowedRoles={['student']}>
          <StudentProfile />
        </PrivateRoute>
      } />
      <Route path="/student/timetable" element={
        <PrivateRoute allowedRoles={['student']}>
          <StudentTimetable />
        </PrivateRoute>
      } />
      <Route path="/student/notices" element={
        <PrivateRoute allowedRoles={['student']}>
          <StudentNotices />
        </PrivateRoute>
      } />
      <Route path="/student/settings" element={
        <PrivateRoute allowedRoles={['student']}>
          <StudentSettings />
        </PrivateRoute>
      } />
      <Route path="/student/attendance" element={
        <PrivateRoute allowedRoles={['student']}>
          <StudentAttendance />
        </PrivateRoute>
      } />
      
      {/* Parent Routes */}
      <Route path="/parent" element={
        <PrivateRoute allowedRoles={['parent']}>
          <ParentDashboard />
        </PrivateRoute>
      } />
      <Route path="/parent/results" element={
        <PrivateRoute allowedRoles={['parent']}>
          <ParentResults />
        </PrivateRoute>
      } />
      <Route path="/parent/attendance" element={
        <PrivateRoute allowedRoles={['parent']}>
          <ParentAttendance />
        </PrivateRoute>
      } />
      <Route path="/parent/fees" element={
        <PrivateRoute allowedRoles={['parent']}>
          <ParentFees />
        </PrivateRoute>
      } />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
